import { useEffect, useRef, useState, type KeyboardEvent } from "react"
import { track } from "@/lib/analytics"
import { Link, useParams } from "react-router-dom"
import { useSessionAnalytics } from "@/lib/useSessionAnalytics"
import { NiraBar } from "../nira2/ds/NiraBar"
import { usePageTitle, useStoredTheme } from "../nira2/answer/hooks"
import "../nira2/answer/answer.css"
import "../nira2/answer/answer-v4.css"
import "../nira2/case/case.css"
import { SimBanner } from "../oio-sim/SimBanner"
import type { MarginLink, SimEngine, Stages } from "./engine"
import { DraftMatter } from "./DraftMatter"
import { EditLimitLine } from "./editLimit"
import { draftProgress } from "./model"
import { FeedbackPanel, FeedbackThumbs } from "../feedback/Feedback"
import type { FeedbackRef } from "../feedback/store"
import "./decisions.css"
import "./draftpage.css"

const newId = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`)

/**
 * The draft beside its reasons (handoff 4, Sim-Draft), for either order: section tabs with their
 * self-check dots, the draft in serif, and in the margin each finding's row that produced it, with
 * the way back to change it. Export, copy and print in the bar. The banner stays: a simulated draft,
 * never an order.
 */
export default function DraftPage({ engine }: { engine: SimEngine }) {
  const { threadId = "" } = useParams<{ threadId: string }>()
  useSessionAnalytics(engine.app, threadId)
  useStoredTheme()
  const view = engine.useThread(threadId)
  const title = view?.title || engine.untitled
  usePageTitle(`${title} · the draft`)
  const { download, exporting, exportError } = engine.useDraftExport(threadId, title)
  const { draft, ids } = engine.useDraftSections(threadId)
  const progress = draftProgress(draft, view?.status ?? "")
  const [copied, setCopied] = useState(false)
  const { DraftPaper } = engine
  // One rating for the finished draft, not one per section.
  const feedback: FeedbackRef | null =
    progress.ready && threadId && view?.status !== "running"
      ? { app: engine.app, sessionId: threadId, target: "draft" }
      : null

  // The draft opened (once per visit to this page), not merely the page loaded while it is still being written.
  const opened = useRef(false)
  useEffect(() => {
    if (!progress.ready || opened.current) return
    opened.current = true
    track("sim_draft_opened", { sim: engine.kind })
  }, [progress.ready, engine.kind])

  useEffect(() => {
    if (view) return
    const ac = new AbortController()
    engine.hydrate(threadId, ac.signal).catch(() => undefined)
    engine.hydrateDocs(threadId, ac.signal).catch(() => undefined)
    return () => ac.abort()
  }, [engine, threadId, view])

  const stages: Stages = view?.stages ?? {}
  const checks = new Map((stages.drafting?.rows ?? []).map((r) => [r.id, r]))
  const back = `${engine.base}/${encodeURIComponent(threadId)}`
  const notes = (stages.drafting?.rows ?? []).filter((r) => r.text === "a note")
  const label = (id: string) => engine.sectionLabel(id, ids, draft?.titles)

  const margin = (sectionId: string) => {
    const lines: React.ReactNode[] = engine.margin(sectionId, stages).map((m) => <MarginNote key={m.anchor} link={m} back={back} />)
    const check = checks.get(sectionId)
    if (check?.text === "a note" && check.sub) lines.push(<p key="c" className="nsdr-side warn"><b>Self-check</b><br />{check.sub}</p>)
    return lines.length ? <>{lines}</> : null
  }

  const copy = async () => {
    const text = ids.map((id) => draft?.sections[id]?.markdown ?? "").filter((x) => x.trim()).join("\n\n")
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch { /* the browser said no; Export still works */ }
  }

  return (
    <div className="na2 na4 ncf nsdr">
      <SimBanner />
      <NiraBar
        crumbs={[{ label: "Order simulator", to: "/simulator" }, { label: title, to: back }, { label: "The draft" }]}
        actions={progress.ready ? (
          <>
            {feedback ? <FeedbackThumbs fb={feedback} /> : null}
            <button type="button" className="nr-bar-act" onClick={() => void copy()}><span>{copied ? "Copied" : "Copy"}</span></button>
            <button type="button" className="nr-bar-act" onClick={() => window.print()}><span>Print</span></button>
            <button type="button" className="nr-bar-act" data-variant="primary" disabled={exporting} onClick={() => void download()}><span>{exporting ? "Building…" : "Export .docx"}</span></button>
          </>
        ) : undefined}
      />
      {feedback ? <FeedbackPanel fb={feedback} /> : null}
      {progress.ready ? (
        <div className="nsdr-wrap">
          <aside className="nsdr-tabs">
            <Link className="nsdr-back" to={back}>← Decisions</Link>
            <nav aria-label="Sections">
              {ids.map((id) => {
                const check = checks.get(id)
                return (
                  <a key={id} href={`#${engine.sectionAnchor(id)}`} className="nsdr-tab">
                    <span>{label(id)}</span>
                    {check ? <span className={`nsdr-dot ${check.text === "a note" ? "note" : check.text === "checked" ? "ok" : ""}`} aria-label={check.text} /> : null}
                  </a>
                )
              })}
            </nav>
            <p className="ncf-eyebrow">Self-check</p>
            <p className="nsdr-check">
              {!stages.drafting ? "Not run yet." : notes.length ? `${notes.length} ${notes.length === 1 ? "note" : "notes"}: ${notes.map((n) => label(n.id)).join(", ")}. Everything else passed.` : stages.drafting.rows.every((r) => r.text === "checked") ? engine.passedLine : "Written; the check runs after the last section."}
            </p>
          </aside>
          <main className="nsdr-body">
            {exportError ? <p className="ncf-said" role="alert">{exportError}</p> : null}
            <DraftPaper threadId={threadId} title={title} aside={margin} />
            <EditLimitLine threadId={threadId} />
          </main>
        </div>
      ) : (
        <main className="nsdr-progress">
          <p className="ncf-msg" role="status">
            {view ? progress.label : "Opening…"}
            <br />
            <Link to={back}>Back to decisions</Link>
          </p>
        </main>
      )}
      {progress.ready ? <Rewrite engine={engine} threadId={threadId} disabled={view?.status === "running"} /> : null}
      {view?.snapshotLoaded ? <DraftMatter key={threadId} kind={engine.kind} threadId={threadId} caseId={view.caseId} placement="float" /> : null}
    </div>
  )
}

function MarginNote({ link, back }: { link: MarginLink; back: string }) {
  return (
    <p className="nsdr-side">
      <b>From {link.from} · {link.head}</b>
      <br /><Link to={`${back}#${link.anchor}`}>{link.action}</Link>
    </p>
  )
}

/** Whole-section asks: "Shorten the findings, add headings, change the tone". */
function Rewrite({ engine, threadId, disabled }: { engine: SimEngine; threadId: string; disabled: boolean }) {
  const [text, setText] = useState("")
  const [busy, setBusy] = useState(false)
  const go = async () => {
    const q = text.trim()
    if (!q || busy || disabled) return
    setBusy(true)
    try {
      await engine.api.postTurn(threadId, q, newId())
      setText("")
    } finally {
      setBusy(false)
    }
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) { e.preventDefault(); void go() } }
  return (
    <div className="nsd-fu">
      <div>
        <input aria-label="Rewrite the draft" placeholder={disabled ? "Rewriting…" : "Shorten the findings, add headings, change the tone…"} value={text} disabled={disabled}
          onChange={(e) => setText(e.target.value)} onKeyDown={onKey} />
        <button type="button" disabled={disabled || busy || !text.trim()} onClick={() => void go()}>Rewrite</button>
      </div>
    </div>
  )
}
